import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FolderOpen, BookOpen, Wrench, ArrowRight, FileText, Users, Database } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const sections = [
    {
      id: 'project-files',
      icon: FolderOpen,
      title: t('projectFiles') || 'Project Files',
      description: t('projectFilesDesc') || 'Manage project folders with drawings, documents, and files',
      color: 'from-blue-500 to-blue-600',
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      path: '/project-files',
    },
    {
      id: 'compliance-library',
      icon: BookOpen,
      title: t('complianceLibrary') || 'Compliance Library',
      description: t('complianceLibraryDesc') || 'Access building codes and regulations by state and type',
      color: 'from-purple-500 to-purple-600',
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      path: '/compliance-library',
    },
    {
      id: 'project-builder',
      icon: Wrench,
      title: t('projectBuilder') || 'Project Builder',
      description: t('projectBuilderDesc') || 'Create and manage architectural documentation projects',
      color: 'from-green-500 to-green-600',
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      path: '/projects',
    },
  ];

  const stats = [
    { icon: FileText, label: 'Active Projects', value: '12' },
    { icon: FolderOpen, label: 'Documents', value: '245' },
    { icon: BookOpen, label: 'Compliance Files', value: '89' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Hero Section with Background Image */}
      <div className="relative h-[60vh] min-h-[500px] overflow-hidden">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1721244654346-9be0c0129e36?q=80&w=2000)',
          }}
        >
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/80 to-slate-900/70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl">
              {/* Logo/Brand */}
              <div className="mb-8 flex items-center space-x-4">
                <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">MB</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-white tracking-tight">
                    BuildPlan Manager
                  </h1>
                  <p className="text-blue-200 text-lg">
                    Professional Document Management
                  </p>
                </div>
              </div>

              {/* Tagline */}
              <p className="text-2xl text-slate-200 mb-8 leading-relaxed">
                {t('landingTagline') || 'Streamline your architectural project documentation with intelligent organization and compliance management'}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/projects')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {t('getStarted') || 'Get Started'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/compliance-library')}
                  className="border-white text-white hover:bg-white/10"
                >
                  {t('browseLibrary') || 'Browse Library'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Sections Cards */}
      <div className="container mx-auto px-6 -mt-20 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <Card
                key={section.id}
                className="group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-2 border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden"
                onClick={() => navigate(section.path)}
              >
                <div className={`h-2 bg-gradient-to-r ${section.color}`}></div>
                <div className="p-8">
                  {/* Icon */}
                  <div className={`w-16 h-16 ${section.bgColor} dark:bg-slate-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <Icon className={`h-8 w-8 ${section.iconColor}`} />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                    {section.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
                    {section.description}
                  </p>

                  {/* CTA */}
                  <div className="flex items-center text-blue-600 dark:text-blue-400 font-semibold group-hover:translate-x-2 transition-transform">
                    {t('access') || 'Access'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800 dark:to-blue-900/20 rounded-2xl p-8 mb-16">
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 text-center">
            {t('quickStats') || 'Quick Stats'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-3">
                    <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
